import React, { useRef } from "react";
import img from "../../../constant";
import { X, CheckSquare } from "lucide-react";

interface PreLiveContentProps {
  clientName: string;
  representativeName: string;
  representativeRole: string;
  date: string;
  handoverDate: string;
  uploadedSignature?: File | null;
  signaturePreview?: string | null;
  onFileUpload?: (file: File) => void;
  onRemoveSignature?: () => void;
  readOnly?: boolean;
}

interface CheckItemProps {
  children: React.ReactNode;
}

const CheckItem: React.FC<CheckItemProps> = ({ children }) => (
  <li className="flex items-start gap-2 py-1.5">
    <CheckSquare size={16} className="text-primary mt-0.5 shrink-0" />
    <span className="text-[13px] text-gray-700">{children}</span>
  </li>
);

const PreLiveContent: React.FC<PreLiveContentProps> = ({
  clientName,
  date,
  handoverDate,
  representativeName,
  representativeRole,
  uploadedSignature,
  signaturePreview,
  onFileUpload,
  onRemoveSignature,
  readOnly = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        alert("Please upload a valid image file (JPG, JPEG, or PNG)");
        return;
      }
      onFileUpload?.(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveSignature?.();
  };

  return (
    <div className="font-abel text-gray-700 text-sm leading-relaxed space-y-6 p-8 bg-white shadow-sm rounded-lg border border-gray-100 max-w-[1024px] mx-auto">
      {/* Branded Header */}
      <div className="text-center mb-8 border-b pb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">25th Suite</h1>
        <h2 className="text-xl font-semibold text-[#2D6EEF] mb-4">
          Pre-Live Checklist &amp; Sign-Off
        </h2>
        <div className="mt-4 space-y-1 text-sm text-gray-600">
          <p>
            <span className="font-semibold text-gray-800">Client Organization:</span>{" "}
            <span className="text-primary font-bold">{clientName}</span>
          </p>
          <p>
            <span className="font-semibold text-gray-800">Assessment Date:</span>{" "}
            {handoverDate}
          </p>
        </div>
      </div>

      {/* Overview & Purpose */}
      <section>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-5">
          <h3 className="font-bold text-sm mb-2 text-blue-900 uppercase tracking-wider">
            Overview &amp; Purpose
          </h3>
          <p className="text-gray-700 text-sm leading-relaxed">
            The pre-live checklist must be completed by both the **Provider (25th Suite)** and **Client** before go-live is confirmed. Neither side can trigger go-live without the other completing their items. The checklist is captured in Step I of the onboarding form and completed in the platform during implementation.
          </p>
        </div>
      </section>

      {/* Two Sides Checklist Table */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
          <h3 className="font-bold text-base mb-3 text-gray-900 border-b pb-2">
            Client-Side Checklist Items
          </h3>
          <ul className="space-y-1 pl-1">
            <CheckItem>
              Client L0 signs the go-live readiness declaration.
            </CheckItem>
            <CheckItem>
              Core user profiles and business unit details mapped and verified.
            </CheckItem>
            <CheckItem>
              Designated IT and department leads credentials generated and tested.
            </CheckItem>
            <CheckItem>
              Final payment schedule and billing details reviewed.
            </CheckItem>
            <CheckItem>
              Primary admin training sessions attended.
            </CheckItem>
          </ul>
        </div>

        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
          <h3 className="font-bold text-base mb-3 text-gray-900 border-b pb-2">
            Provider-Side Checklist Items
          </h3>
          <ul className="space-y-1 pl-1">
            <CheckItem>
              CTO reviews, signs, and generates the Go-Live Pack.
            </CheckItem>
            <CheckItem>
              System configurations, white-label branding, and integrations verified.
            </CheckItem>
            <CheckItem>
              Secure credential delivery process confirmed.
            </CheckItem>
            <CheckItem>
              Escalation support contacts and SLA guidelines mapped.
            </CheckItem>
            <CheckItem>
              Onboarding checklist (first 30 days) set up for delivery.
            </CheckItem>
          </ul>
        </div>
      </section>

      {/* Go-Live Confirmation Policy */}
      <section className="border border-gray-200 rounded-lg p-5 bg-white">
        <h3 className="font-bold text-base mb-3 text-gray-900 border-b pb-2">
          Go-Live Confirmation Policy
        </h3>
        <p className="text-gray-700 text-sm mb-4 leading-relaxed">
          Go-live is officially confirmed when:
        </p>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
          <li>All pre-live checklist items are complete.</li>
          <li>Client L0 signs the go-live readiness declaration.</li>
          <li>CEO reviews and approves.</li>
          <li>CTO signs the go-live pack.</li>
        </ol>
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-md">
          <strong>PROTECTIVE TRIGGER:</strong> Go-live is automatically confirmed after 5 business days if the client has been notified of readiness and has not responded. This protects the 30% balance invoice trigger.
        </div>
      </section>

      {/* Go-Live Pack Details */}
      <section className="border border-gray-200 rounded-lg p-5 bg-white">
        <h3 className="font-bold text-base mb-3 text-gray-900 border-b pb-2">
          Go-Live Pack Delivery Package
        </h3>
        <p className="text-sm text-gray-700 mb-3">
          The go-live pack consists of <strong>ONE email</strong> with <strong>ONE PDF attachment</strong>, branded and auto-generated, containing:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#2D6EEF] rounded-full shrink-0" />
              <span>Go-live confirmation letter (dated, signed by CTO)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#2D6EEF] rounded-full shrink-0" />
              <span>Summary of work completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#2D6EEF] rounded-full shrink-0" />
              <span>Days taken vs days quoted (e.g. Quoted: 60, Completed: 51)</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#2D6EEF] rounded-full shrink-0" />
              <span>Active subscription summary (products, tier, billing cycle)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#2D6EEF] rounded-full shrink-0" />
              <span>Support contact details and SLA summary</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#2D6EEF] rounded-full shrink-0" />
              <span>Client-facing onboarding checklist (first 30 days)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Signatures Section */}
      <div className="mt-8 border-t pt-6">
        <h3 className="font-bold text-lg mb-4 text-gray-900">Authorization &amp; Execution</h3>
        <p className="text-xs text-gray-500 mb-6">
          IN WITNESS WHEREOF, both parties have caused this Pre-Live checklist to be executed by their duly authorized representatives.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Provider side */}
          <div className="border p-5 rounded-lg bg-gray-50">
            <p className="font-bold text-sm text-[#2D6EEF] mb-3">For: 25th Suite (Provider)</p>
            <div className="space-y-3">
              <img src={img.atsuSign} alt="Atsu Sign" className="h-14 object-contain" />
              <p className="border-b border-gray-300 pb-1 text-sm">
                <strong>Name:</strong> Atsu Emmanuel
              </p>
              <p className="border-b border-gray-300 pb-1 text-sm">
                <strong>Title:</strong> CTO
              </p>
              <p className="border-b border-gray-300 pb-1 text-sm">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>

          {/* Client side */}
          <div className="border p-5 rounded-lg bg-gray-50">
            <p className="font-bold text-sm text-gray-800 mb-3">For: {clientName} (Client L0 Representative)</p>
            <div className="space-y-3">
              <div className="h-14 w-full">
                {readOnly ? (
                  <div className="h-14 flex items-end">
                    <span className="border-b border-gray-300 block w-48 text-gray-400 text-xs pb-1">
                      Signature Pending
                    </span>
                  </div>
                ) : !signaturePreview ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Upload Signature
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="block w-full text-xs text-gray-500
                        file:mr-3 file:py-1 file:px-3
                        file:rounded file:border-0
                        file:text-xs file:font-semibold
                        file:bg-[#2D6EEF] file:text-white
                        hover:file:bg-blue-600
                        cursor-pointer"
                    />
                  </div>
                ) : (
                  <div
                    className="relative group cursor-pointer inline-block"
                    onClick={handleImageClick}
                  >
                    <img
                      src={signaturePreview}
                      alt="Uploaded Signature"
                      className="h-14 object-contain"
                    />
                    <button
                      onClick={handleRemoveClick}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5
                        opacity-0 group-hover:opacity-100 transition-opacity duration-200
                        hover:bg-red-600"
                      aria-label="Remove signature"
                    >
                      <X size={14} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
              <p className="border-b border-gray-300 pb-1 text-sm">
                <strong>Name: </strong>
                {representativeName}
              </p>
              <p className="border-b border-gray-300 pb-1 text-sm">
                <strong>Title: </strong>
                {representativeRole}
              </p>
              <p className="border-b border-gray-300 pb-1 text-sm">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreLiveContent;
